import { campaignsControllerListCampaigns } from '@/generated/korza-api-client';
import type {
  CampaignDto,
  ListCampaignsResponseDto,
} from '@/generated/korza-api-client/types.gen';
import {
  useInfiniteQuery,
  useQuery,
  type InfiniteData,
} from '@tanstack/react-query';
import { useTenant } from './use-tenant';

export type CampaignsFilters = {
  launchDateMin?: string;
  launchDateMax?: string;
  hasBeenSent?: 'true' | 'false';
  audienceId?: string | 'null';
  importBatchId?: string;
  /** One or more statuses (`?status=` repeated); absent = every status. */
  status?: Array<CampaignDto['status']>;
  /** Name contains, case-insensitive (backend trims, 1–100 characters). */
  search?: string;
};

export const CAMPAIGNS_PAGE_SIZE = 50;

function buildCampaignsQuery(
  filters: CampaignsFilters,
  limit: number,
  offset: number
) {
  return {
    audienceId: filters.audienceId,
    hasBeenSent: filters.hasBeenSent,
    importBatchId: filters.importBatchId,
    launchDateMin: filters.launchDateMin,
    launchDateMax: filters.launchDateMax,
    status: filters.status,
    search: filters.search,
    limit,
    offset,
  };
}

export async function listCampaignsPage(
  tenantSlug: string,
  filters: CampaignsFilters,
  limit: number,
  offset: number
): Promise<ListCampaignsResponseDto> {
  const res = await campaignsControllerListCampaigns({
    path: { tenantSlug },
    query: buildCampaignsQuery(filters, limit, offset),
    throwOnError: true,
  });
  if (!res.data) {
    throw new Error('Failed to load campaigns');
  }
  return res.data;
}

export async function fetchAllCampaigns(
  tenantSlug: string,
  filters: CampaignsFilters,
  pageSize = CAMPAIGNS_PAGE_SIZE
): Promise<CampaignDto[]> {
  const all: CampaignDto[] = [];
  let offset = 0;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const page = await listCampaignsPage(tenantSlug, filters, pageSize, offset);
    all.push(...page.data);
    if (page.data.length === 0 || offset + page.data.length >= page.total) {
      break;
    }
    offset += pageSize;
  }

  return all;
}

export function useCampaigns(
  filters: CampaignsFilters,
  limit: number,
  offset: number,
  enabled = true
) {
  const tenant = useTenant();

  return useQuery<ListCampaignsResponseDto, Error>({
    queryKey: ['campaigns', tenant.slug, filters, limit, offset],
    queryFn: () => listCampaignsPage(tenant.slug, filters, limit, offset),
    enabled: !!tenant.slug && enabled,
  });
}

export function useTenantSentCampaigns() {
  const tenant = useTenant();

  return useQuery<CampaignDto[], Error>({
    queryKey: ['campaigns', tenant.slug, 'sent', 'all'],
    queryFn: () => fetchAllCampaigns(tenant.slug, { hasBeenSent: 'true' }),
    enabled: !!tenant.slug,
  });
}

export function useInfiniteCampaigns(
  filters: CampaignsFilters,
  enabled = true
) {
  const tenant = useTenant();

  return useInfiniteQuery<
    ListCampaignsResponseDto,
    Error,
    InfiniteData<ListCampaignsResponseDto>,
    readonly unknown[],
    number
  >({
    queryKey: ['campaigns', tenant.slug, filters, 'infinite'],
    queryFn: ({ pageParam }) =>
      listCampaignsPage(tenant.slug, filters, CAMPAIGNS_PAGE_SIZE, pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.offset + lastPage.data.length;
      if (lastPage.data.length === 0 || nextOffset >= lastPage.total) {
        return undefined;
      }
      return nextOffset;
    },
    enabled: !!tenant.slug && enabled,
  });
}
